import { useEffect, useState } from "react";
import {
  BOTTOM_DOCK_TAB_KEY,
  BOTTOM_TABS,
  LEGACY_TAB_MAP,
  RAIL_OPEN_KEY,
  type BottomTab,
} from "./shellState";

export function useShellLayoutState(
  workspaceId: string | undefined,
  autoCollapseLeft: boolean,
  autoCollapseRight: boolean,
  codexEnabled: boolean,
) {
  const [leftOpen, setLeftOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(RAIL_OPEN_KEY(workspaceId, "left"));
    return stored === null ? true : stored === "1";
  });
  const [rightOpen, setRightOpen] = useState<boolean>(() => {
    if (typeof window === "undefined") return true;
    const stored = window.localStorage.getItem(RAIL_OPEN_KEY(workspaceId, "right"));
    return stored === null ? true : stored === "1";
  });
  const [bottomDockTab, setBottomDockTab] = useState<BottomTab | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem(BOTTOM_DOCK_TAB_KEY(workspaceId));
    if (!stored) return null;
    const migrated = LEGACY_TAB_MAP[stored];
    if (migrated && BOTTOM_TABS.some((t) => t.id === migrated)) return migrated;
    return null;
  });

  useEffect(() => {
    if (autoCollapseLeft) setLeftOpen(false);
  }, [autoCollapseLeft]);
  useEffect(() => {
    if (autoCollapseRight) setRightOpen(false);
  }, [autoCollapseRight]);
  useEffect(() => {
    if (autoCollapseLeft) setBottomDockTab(null);
  }, [autoCollapseLeft]);
  useEffect(() => {
    if (!codexEnabled && bottomDockTab === "codex") setBottomDockTab(null);
  }, [codexEnabled, bottomDockTab]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(RAIL_OPEN_KEY(workspaceId, "left"), leftOpen ? "1" : "0");
  }, [leftOpen, workspaceId]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(RAIL_OPEN_KEY(workspaceId, "right"), rightOpen ? "1" : "0");
  }, [rightOpen, workspaceId]);
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (bottomDockTab) {
      window.localStorage.setItem(BOTTOM_DOCK_TAB_KEY(workspaceId), bottomDockTab);
    } else {
      window.localStorage.removeItem(BOTTOM_DOCK_TAB_KEY(workspaceId));
    }
  }, [bottomDockTab, workspaceId]);

  return { leftOpen, setLeftOpen, rightOpen, setRightOpen, bottomDockTab, setBottomDockTab };
}

export function persistSize(key: string) {
  return (size: number) => {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(key, String(size));
  };
}

export function readSize(key: string, fallback: number) {
  if (typeof window === "undefined") return fallback;
  const stored = window.localStorage.getItem(key);
  const parsed = stored ? Number(stored) : NaN;
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}
